// Master Admin → Email Settings page (SMTP + Resend toggle, test send)
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Save, Send, Eye, EyeOff } from "lucide-react";
import { API, getToken } from "../../lib/auth";
import { useConfirm } from "../../components/ConfirmDialog";

const cfg = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const cls = {
  card: "bg-white rounded-2xl border border-black/[0.06]",
  input: "w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm bg-white",
  label: "block text-[11px] font-bold uppercase tracking-wider text-black/55 mb-1.5",
  primary: "inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white text-sm font-bold hover:opacity-95 shadow-sm",
};

export default function AdminEmailSettings() {
  const [s, setS] = useState(null);
  const [show, setShow] = useState(false);
  const [pwSecret, setPwSecret] = useState("");
  const [resendSecret, setResendSecret] = useState("");
  const [testTo, setTestTo] = useState("");
  const confirm = useConfirm();
  const load = () => axios.get(`${API}/admin/email-settings`, cfg()).then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  if (!s) return <div className="p-8">Loading…</div>;

  const save = async () => {
    if (!(await confirm({ title: "Save email settings?", message: "Notifications will use these credentials immediately." }))) return;
    try {
      const payload = { ...s };
      delete payload.smtp_password_masked;
      delete payload.resend_api_key_masked;
      if (pwSecret) payload.smtp_password = pwSecret;
      if (resendSecret) payload.resend_api_key = resendSecret;
      await axios.patch(`${API}/admin/email-settings`, payload, cfg());
      toast.success("Saved"); setPwSecret(""); setResendSecret(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const sendTest = async () => {
    if (!testTo.includes("@")) return toast.error("Enter a valid recipient email");
    if (!(await confirm({ title: "Send test email?", message: `To ${testTo}` }))) return;
    try {
      const r = await axios.post(`${API}/admin/email-settings/test`, { to: testTo }, cfg());
      if (r.data?.sent) toast.success(`Sent via ${r.data.provider}`);
      else toast.error(r.data?.reason || "Failed to send");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-email-page" className="p-6 lg:p-8">
      <div className="mb-6">
        <h1 className="font-display font-extrabold text-3xl tracking-tight">Email Settings</h1>
        <p className="text-xs text-black/55 mt-1">Master Admin / Email — configure SMTP or Resend for system notifications</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <div className={`${cls.card} p-6`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-[#E8F5E9] text-[#1FA868] flex items-center justify-center"><Mail size={18} /></div>
            <div className="flex-1"><h3 className="font-extrabold">General</h3><p className="text-xs text-black/55">Sender identity & provider</p></div>
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} data-testid="es-enabled" /> <span className="font-semibold">Enabled</span></label>
          </div>
          <div className="space-y-3">
            <div>
              <label className={cls.label}>Provider</label>
              <div className="flex gap-2">
                {["smtp", "resend"].map((p) => (
                  <button key={p} onClick={() => setS({ ...s, provider: p })} data-testid={`es-provider-${p}`}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-bold capitalize border-2 ${s.provider === p ? "border-[#1FA868] bg-[#E8F5E9] text-[#1FA868]" : "border-black/10 text-black/55"}`}>{p}</button>
                ))}
              </div>
            </div>
            <div><label className={cls.label}>From Name</label><input className={cls.input} value={s.from_name || ""} onChange={(e) => setS({ ...s, from_name: e.target.value })} /></div>
            <div><label className={cls.label}>From Email</label><input className={cls.input} value={s.from_email || ""} onChange={(e) => setS({ ...s, from_email: e.target.value })} data-testid="es-from-email" /></div>
          </div>
        </div>

        {s.provider === "smtp" && (
          <div className={`${cls.card} p-6`}>
            <div className="flex items-center gap-3 mb-5"><div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center"><Mail size={18} /></div><h3 className="font-extrabold">SMTP Configuration</h3></div>
            <div className="space-y-3">
              <div><label className={cls.label}>SMTP Host</label><input className={cls.input} value={s.smtp_host || ""} onChange={(e) => setS({ ...s, smtp_host: e.target.value })} placeholder="smtp.gmail.com" data-testid="es-smtp-host" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={cls.label}>Port</label><input className={cls.input} type="number" value={s.smtp_port || 587} onChange={(e) => setS({ ...s, smtp_port: Number(e.target.value) })} data-testid="es-smtp-port" /></div>
                <div><label className={cls.label}>TLS</label><label className="flex items-center gap-2 mt-3"><input type="checkbox" checked={s.smtp_use_tls !== false} onChange={(e) => setS({ ...s, smtp_use_tls: e.target.checked })} /> <span className="text-sm">STARTTLS</span></label></div>
              </div>
              <div><label className={cls.label}>Username</label><input className={cls.input} value={s.smtp_username || ""} onChange={(e) => setS({ ...s, smtp_username: e.target.value })} data-testid="es-smtp-user" /></div>
              <div>
                <label className={cls.label}>Password {s.smtp_password_masked && <span className="text-black/45 font-normal normal-case">(current: {s.smtp_password_masked})</span>}</label>
                <div className="relative">
                  <input type={show ? "text" : "password"} className={`${cls.input} pr-10`} value={pwSecret} onChange={(e) => setPwSecret(e.target.value)} placeholder={s.smtp_password_masked ? "Leave blank to keep current" : "App password"} data-testid="es-smtp-pw" />
                  <button onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-black/45">{show ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {s.provider === "resend" && (
          <div className={`${cls.card} p-6`}>
            <div className="flex items-center gap-3 mb-5"><div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center"><Mail size={18} /></div><h3 className="font-extrabold">Resend Configuration</h3></div>
            <div>
              <label className={cls.label}>API Key {s.resend_api_key_masked && <span className="text-black/45 font-normal normal-case">(current: {s.resend_api_key_masked})</span>}</label>
              <input type="password" className={cls.input} value={resendSecret} onChange={(e) => setResendSecret(e.target.value)} placeholder="re_..." data-testid="es-resend-key" />
              <p className="text-[11px] text-black/55 mt-2">Get your key at <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-[#1FA868] underline">resend.com/api-keys</a></p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button onClick={save} data-testid="es-save" className={cls.primary}><Save size={14} /> Save Settings</button>
        <div className="flex-1" />
        <input className={cls.input + " max-w-xs"} placeholder="recipient@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="es-test-to" />
        <button onClick={sendTest} data-testid="es-test-send" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-bold hover:bg-black/[0.02]"><Send size={13} /> Send Test Email</button>
      </div>
    </div>
  );
}
